import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, HelpCircle, Loader2 } from "lucide-react";

// Stato del canarino, letto dal file statico firmato. Statico e non un endpoint per un
// motivo: un endpoint che dice «tutto bene» è esattamente ciò che un server compromesso
// continuerebbe a dire. Qui conta la firma e la data: se nessuno rifirma, si vede.
export default function CanaryStatus({ compact = false }) {
  const [s, setS] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch("/downloads/canary-status.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => {
        if (!alive) return;
        const days = parseInt(j.days_left, 10);
        setS({
          state: j.state === "ok" && j.sig_valid && days >= 0 ? "ok" : "alarm",
          days: isNaN(days) ? null : days,
          signedAt: j.signed_at || "",
          expiresAt: j.expires_at || "",
          reason: j.reason || null,
        });
      })
      .catch(() => alive && setS({ state: "unknown", days: null, signedAt: "", expiresAt: "", reason: null }));
    return () => { alive = false; };
  }, []);

  if (!s) {
    return (
      <span data-testid="canary-loading" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#666]">
        <Loader2 className="h-3 w-3 animate-spin" /> canarino
      </span>
    );
  }

  const map = {
    ok: { Icon: ShieldCheck, color: "var(--lt-primary)", label: "Canarino · Ok" },
    alarm: { Icon: ShieldAlert, color: "#ff3333", label: "Canarino · Allarme" },
    unknown: { Icon: HelpCircle, color: "#888", label: "Canarino · Non verificabile" },
  };
  const { Icon, color, label } = map[s.state];

  if (compact) {
    return (
      <span data-testid="canary-badge-compact" title={s.expiresAt ? `Scade il ${s.expiresAt}` : ""}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color }}>
        <Icon className="h-3.5 w-3.5" /> {label}
      </span>
    );
  }

  return (
    <div data-testid="canary-badge" className="border p-3" style={{ borderColor: `${color}55`, background: `${color}0f` }}>
      <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color }}>
        <Icon className="h-4 w-4" /> {label}
      </div>
      <p className="mt-2 font-mono text-[11px] leading-relaxed text-[#8a8a8a]" data-testid="canary-detail">
        {s.state === "ok" && (
          <>Firmato il <b className="text-[#d4d4d4]">{s.signedAt}</b>, valido fino al{" "}
            <b className="text-[#d4d4d4]">{s.expiresAt}</b>
            {s.days != null ? <> · restano {s.days} giorni</> : null}.
            {s.days != null && s.days <= 14 ? (
              <> <b className="text-white">Da rifirmare entro il {s.expiresAt}.</b></>
            ) : null} Nessuna richiesta di dati,
            nessun ordine di silenzio, nessuna backdoor. Se questa riga non viene rifirmata alla
            scadenza, l'assenza vale come segnale.</>
        )}
        {s.state === "alarm" && (
          <>Il canarino è in <b className="text-[#ffb4b4]">allarme</b>
            {s.reason ? <> — motivo dichiarato: {s.reason}</> : null}. Leggete il Transparency
            Report prima di continuare a usare il servizio.</>
        )}
        {s.state === "unknown" && (
          <>Lo stato non è raggiungibile da questo browser. Non scriviamo «verificato» quando non
            abbiamo verificato niente: l'unica risposta onesta qui è «non lo so».</>
        )}
      </p>
      <a href="/downloads/canary.txt" data-testid="canary-raw"
         className="mt-2 inline-block font-mono text-[10px] uppercase tracking-[0.25em] text-[#666] underline hover:text-[#a3a3a3]">
        testo firmato + firma
      </a>
    </div>
  );
}
